<template>
  <s-modal
    class="group-management-modal"
    v-model="showModal"
    ok-confirm-message-code="MC011"
    cancel-confirm-message-code="MC006"
    size="md"
    :is-loading="loading"
    :title="title"
    :ok-title="$t('general.confirm')"
    :ok-disabled="$v.$invalid"
    :ok-promise="save"
    :confirm-type="confirmationTypes.YES_NO"
    @cancel="cancel"
    @show="onShowHandler"
  >
    <s-fieldset
      id="fieldset"
      class="group-management-modal__fieldset"
      :legend="$t('engineering.cooling-maintenance.inform-groups')"
    >
      <ul class="group-management-modal__groups">
        <li
          class="group-management-modal__groups-item"
          v-for="item in groups"
          :key="item.id"
        >
          <b-checkbox
            :checked="selectedGroups.includes(item.id)"
            @change="selectGroup($event, item.id)"
          >
            {{ item.name }}
          </b-checkbox>
        </li>
      </ul>
    </s-fieldset>
    <div class="mt-3">
      <label>{{ $t('engineering.cooling-maintenance.performance-date') }}</label>
      <s-input-date-picker
        class="group-management-modal__input"
        v-model="actionDateProxy"
        :time-zone="dateOnlyTimeZone"
        :state="inputOptions.actionDate.state"
        :min-date="inputOptions.actionDate.min"
        :max-date="inputOptions.actionDate.max"
        @input="clearDateIsLessThanLastMaintenance"
      />
      <b-form-invalid-feedback class="d-block" v-if="$v.actionDate.$invalid">
        {{
          $t('messages.MA101.message', {
            0: getFormattedDate(minimumPerformanceDate, formatDate),
            1: getFormattedDate(maximumPerformanceDate, formatDate),
          })
        }}
      </b-form-invalid-feedback>
    </div>
    <div
      v-if="isReplacement"
      class="mt-3"
    >
      <label>{{ `${$t('engineering.cooling-maintenance.total-operating-time')}*` }}</label>
      <s-number-input
        class="group-management-modal__input"
        v-model="operationTotalTime"
        :validation="$v.operationTotalTime"
        :invalid="$v.operationTotalTime.$error"
        :numeric-options="configureNumericOptionsPrecision()"
        @input="$v.operationTotalTime.$touch()"
        @blur-native-proxy="$v.operationTotalTime.$touch()"
      />
      <b-form-invalid-feedback v-if="$v.operationTotalTime.$error">
        {{
          $t('messages.MA086.message', {
            minValue: inputOptions.operationTotalTime.min,
            maxValue: $n(inputOptions.operationTotalTime.max),
          })
        }}
      </b-form-invalid-feedback>
    </div>
  </s-modal>
</template>

<script>
import { GenericError } from 'sigma-mappings';
import { required, requiredIf, between } from 'vuelidate/lib/validators';
import modalTypes
  from '@/domain/enums/installation/engineering/coolingMaintenance/groupManagementModalTypes';
import Service
  from '@/domain/services/installation/engineering/coolingMaintenance/coolingMaintenanceService';
import confirmationTypes from '@/constants/confirmationTypes';
import loadingMixin from '@/services/loadingMixin';
import {
  formatter as getFormattedDate,
  isBetween,
  getInitialDate,
  getStartOfDay,
  setToStartOfDay,
} from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';

const dateOnlyTimeZone = timezones.SYSTEM_DATE_ONLY;
const currentDate = getStartOfDay({ timeZone: dateOnlyTimeZone });

export default {
  name: 'GroupManagementModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    modalType: {
      type: String,
      default: '',
      validator: value => Object.values(modalTypes).includes(value),
    },
  },
  mixins: [loadingMixin],
  data: () => ({
    groups: [],
    selectedGroups: [],
    actionDate: currentDate,
    operationTotalTime: null,
    minimumPerformanceDate: getInitialDate({ timeZone: dateOnlyTimeZone }),
    maximumPerformanceDate: currentDate,
    confirmationTypes,
    dateOnlyTimeZone,
    getFormattedDate,
    formatDate: formats.DATE_SHORT,
    dateIsLessThanLastMaintenance: false,
  }),
  validations() {
    return {
      actionDate: {
        required,
        between: isBetween(
          this.minimumPerformanceDate,
          this.maximumPerformanceDate,
        ),
      },
      operationTotalTime: {
        required: requiredIf(() => this.isReplacement),
        between: between(0, 999999),
      },
      selectedGroups: {
        required,
      },
    };
  },
  computed: {
    actionDateProxy: {
      get() {
        return this.actionDate;
      },
      set(value) {
        this.actionDate = value
          ? setToStartOfDay(value, { timeZone: this.dateOnlyTimeZone })
          : null;
      },
    },
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    inputOptions() {
      const dateValidator = this.$v.actionDate.$invalid || this.dateIsLessThanLastMaintenance;
      return {
        operationTotalTime: {
          min: 0,
          max: 999999,
          maxLength: 7,
          state: this.$bsStateValidator(this.$v.operationTotalTime.$error),
        },
        actionDate: {
          min: this.minimumPerformanceDate,
          max: this.maximumPerformanceDate,
          state: this.$bsStateValidator(dateValidator),
        },
      };
    },
    isReplacement() {
      return this.modalType === modalTypes.REPLACEMENT;
    },
    title() {
      const titleByModalType = {
        [modalTypes.MAINTENANCE]: this.$t('engineering.cooling-maintenance.cooling-group-maintenance'),
        [modalTypes.REPLACEMENT]: this.$t('engineering.cooling-maintenance.replace-cooling-group'),
      };
      return titleByModalType[this.modalType] || titleByModalType[0];
    },
  },
  created() {
    this.getGroups();
  },
  methods: {
    configureNumericOptionsPrecision() {
      return {
        decimalPlaces: 0,
      };
    },
    getGroups() {
      this.startLoading();
      Service.getGroups(this.$route.params.assetModuleId).then((response) => {
        this.groups = response;
      }).finally(() => {
        this.endLoading();
      });
    },
    selectGroup(value, groupId) {
      const isGroupSelected = this.selectedGroups.includes(groupId);
      if (!value && isGroupSelected) {
        this.selectedGroups = this.selectedGroups.filter(group => group !== groupId);
        return;
      }
      if (value && !isGroupSelected) {
        this.selectedGroups.push(groupId);
      }
    },
    clearFields() {
      this.selectedGroups = [];
      this.actionDate = currentDate;
      this.operationTotalTime = null;
      this.clearDateIsLessThanLastMaintenance();
      this.$v.$reset();
    },
    cancel() {
      this.clearFields();
      this.showModal = false;
    },
    dateIsLessThanLastMaintenanceExceptionCallback(genericError) {
      const error = (genericError?.validationErrors || [])
        .filter(item => item.messageCode === 'MA099');
      if (error.length) {
        this.dateIsLessThanLastMaintenance = true;
      }
    },
    clearDateIsLessThanLastMaintenance() {
      this.dateIsLessThanLastMaintenance = false;
    },
    onShowHandler() {
      this.$v.$reset();
    },
    postReplacement(payload) {
      return Service.replaceGroup(this.$route.params.assetModuleId, payload)
        .then(() => {
          this.$pushAlert({ code: 'MS032' });
          this.$emit('refresh');
          this.cancel();
        })
        .catch(({ response }) => {
          const genericError = new GenericError(response.data);
          this.dateIsLessThanLastMaintenanceExceptionCallback(genericError);
        });
    },
    reportMaintenance(payload) {
      return Service.reportMaintenance(this.$route.params.assetModuleId, payload)
        .then(() => {
          this.$pushAlert({ code: 'MS032' });
          this.$emit('refresh');
          this.cancel();
        })
        .catch(({ response }) => {
          const genericError = new GenericError(response.data);
          this.dateIsLessThanLastMaintenanceExceptionCallback(genericError);
        });
    },
    save() {
      const payload = {
        groupIds: this.selectedGroups,
        actionDate: this.actionDate,
        operationTotalTime: this.operationTotalTime,
      };
      if (this.isReplacement) {
        return this.postReplacement(payload);
      }
      return this.reportMaintenance(payload);
    },
  },
  watch: {
    modalType(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.clearFields();
      }
    },
    groups: {
      handler(newValue) {
        const fieldset = document.querySelector('#fieldset') || { style: {} };
        if (newValue.length <= 8) {
          fieldset.style['overflow-y'] = 'hidden';
          return;
        }
        fieldset.style['overflow-y'] = 'auto';
      },
      immediate: true,
    },
  },
};
</script>

<style lang="sass" scoped>
.group-management-modal
  &__groups
    padding-left: 10px
    margin-bottom: 0
    column-count: 2
    list-style-type: none

  &__groups-item
    margin-bottom: 15px

  &__fieldset
    padding-top: 10px
    padding-bottom: 10px
    max-height: 175px
    overflow-y: auto

  &__input
    max-width: 30%

  ::v-deep
    .modal-dialog
      max-width: 600px

.manage-fonts_16px
  .group-management-modal
    &__fieldset
      padding-bottom: 15px
      max-height: 190px

    ::v-deep
      .modal-dialog
        max-width: 640px

.manage-fonts_18px
  .group-management-modal
    &__fieldset
      padding-bottom: 20px
      max-height: 205px

    ::v-deep
      .modal-dialog
        max-width: 720px
</style>
